import { StandardUniselection, Unifree } from '@yes.technology/react-toolkit'
import { TextSignatureContainer } from './ActivityInputDraw.styles'
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18n-lite'

const MAX_NAME_LENGTH = 50

export const ALLOWED_SIGNATURE_FONTS = [
  {
    id: 'seaweed-script',
    value: 'Seaweed Script',
    path: '/fonts/seaweedscript-regular.ttf'
  } as const,
  {
    id: 'solitreo',
    value: 'Solitreo',
    path: '/fonts/solitreo-regular.ttf'
  } as const,
  {
    id: 'square-peg',
    value: 'Square Peg',
    path: '/fonts/squarepeg-regular.ttf'
  } as const
]

type AllowedSignatureFont = (typeof ALLOWED_SIGNATURE_FONTS)[number]['value']

const textToImageDataUrl = (
  text: string,
  fontFamily: AllowedSignatureFont,
  maxWidth: number
): string => {
  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')

  if (!context || !text) {
    return ''
  }

  const fontSize = 24
  const margin = 16

  const font = `${fontSize}px ${fontFamily}`
  context.font = font

  const textWidthWithMargins = context.measureText(text).width + margin * 2
  canvas.width = Math.min(maxWidth, textWidthWithMargins)
  canvas.height = fontSize * 1.5

  context.textBaseline = 'middle'
  // after setting canvas dimensions, context is reset, so we need to set font again
  context.font = font

  context.fillText(text, margin, canvas.height / 2, canvas.width - margin * 2)

  return canvas.toDataURL('image/png')
}

const preloadSignatureFonts = async () => {
  await Promise.all(
    ALLOWED_SIGNATURE_FONTS.map(async (font) => {
      const fontFace = new FontFace(font.value, `url("${font.path}")`)
      await fontFace.load()
      document.fonts.add(fontFace)
    })
  )

  return document.fonts.ready
}

const usePreloadSignatureFonts = (enabled: boolean) =>
  useQuery({
    queryKey: ['activity-input-draw', 'signature-fonts'],
    queryFn: preloadSignatureFonts,
    enabled
  })

export type TextSignatureEditorRef = {
  clear: () => void
}

type TextSignatureEditorProps = {
  onChange: (base64: string) => void
  enabled?: boolean
}

const TextSignatureEditor = forwardRef<
  TextSignatureEditorRef,
  TextSignatureEditorProps
>(({ enabled = false, onChange }: TextSignatureEditorProps, ref) => {
  const [fullname, setFullname] = useState('')
  const [font, setFont] = useState(ALLOWED_SIGNATURE_FONTS[0])
  const containerRef = useRef<HTMLDivElement>(null)
  const { isFetching: fontsLoading } = usePreloadSignatureFonts(enabled)

  useEffect(() => {
    if (!containerRef.current || !enabled) return

    if (fontsLoading) {
      onChange('')
    }

    onChange(
      textToImageDataUrl(fullname, font.value, containerRef.current.offsetWidth)
    )
  }, [enabled, font.value, fontsLoading, fullname, onChange])

  useImperativeHandle(ref, () => ({
    clear: () => {
      setFullname('')
      setFont(ALLOWED_SIGNATURE_FONTS[0])
      onChange('')
    }
  }))

  const { t } = useTranslation()

  if (!enabled) {
    return null
  }

  return (
    <TextSignatureContainer ref={containerRef}>
      <Unifree
        required
        label={t('activity-input-draw.type-fullname')}
        placeholder={t('activity-input-draw.type-fullname-placeholder')}
        onChange={setFullname}
        value={fullname}
        maxLength={MAX_NAME_LENGTH}
      />

      <StandardUniselection
        heightClass='regular'
        required
        label={t('activity-input-draw.select-font')}
        selectedOption={font}
        options={fontsLoading ? [] : ALLOWED_SIGNATURE_FONTS}
        statusClass={fontsLoading ? 'processing' : 'default'}
        onSelect={
          setFont as React.ComponentProps<
            typeof StandardUniselection
          >['onSelect']
        }
      />
    </TextSignatureContainer>
  )
})

TextSignatureEditor.displayName = 'TextSignatureEditor'

export default TextSignatureEditor
